import React, { useEffect, useState } from "react";
import { useStore } from './../states/store';
import HLBackendV1Api from '../utils/http/api';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import Swal from 'sweetalert2';
import Crypto from '../crypto';
import { soundLibrary } from "../utils/sfx/sound";
import HLBackendV1 from "../utils/socket/HL_backend_v1";
import AsymmetricBetCrypto from '../crypto/asymmetric-bet-crypto';


function MobileBettingForm({type, onClose}) {
    const { count, betType, userPlaceBet, authUser, currentRound, timerStatus, setUserPendingBets, setBetSuccess } = useStore();
    
    const [bettingAmount, setBettingAmount] = useState('');
    const [isSubmittable, setIsSubmittable] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const [socket, setSocket] = useState(null);

    const computeBettingAmount = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setBettingAmount(value);
        }

        if(bettingAmount >= +process.env.HL_MAXIMUM_BET_LIMIT) {
            setBettingAmount(process.env.HL_MAXIMUM_BET_LIMIT);
            return;
        }
    };

    // Updated to accept value directly
    const computeBettingAmountButton = (value) => {
        if(bettingAmount >= +process.env.HL_MAXIMUM_BET_LIMIT) {
            setBettingAmount(process.env.HL_MAXIMUM_BET_LIMIT);
            return;
        }

        setBettingAmount((prevBettingAmount) => {
            let newAmount = prevBettingAmount === '' ? value : Number(prevBettingAmount) + value;
            return newAmount.toString();
        });

        if(bettingAmount > authUser.balance || bettingAmount > +process.env.HL_MAXIMUM_BET_LIMIT) {
            let maxThreshold = (authUser.balance > +process.env.HL_MAXIMUM_BET_LIMIT) ? +process.env.HL_MAXIMUM_BET_LIMIT: authUser.balance;
            setBettingAmount(maxThreshold);
        }
    };

    const computeBettingAmountButtonAllIn = () => {
        let allInThreshold = (authUser.balance > +process.env.HL_MAXIMUM_BET_LIMIT) ? +process.env.HL_MAXIMUM_BET_LIMIT: authUser.balance;
        setBettingAmount(allInThreshold);
    };

    const multiplyBettingAmount = (multiplier) => {
        if(bettingAmount >= +process.env.HL_MAXIMUM_BET_LIMIT) {
            return;
        }

        setBettingAmount((prevBettingAmount) => {
            const currentAmount = prevBettingAmount === '' ? 0 : Number(prevBettingAmount);
            const newAmount = currentAmount * multiplier;
            return newAmount.toString();
        });

        if(bettingAmount > authUser.balance || bettingAmount > +process.env.HL_MAXIMUM_BET_LIMIT) {
            let maxThreshold = (authUser.balance > +process.env.HL_MAXIMUM_BET_LIMIT) ? +process.env.HL_MAXIMUM_BET_LIMIT: authUser.balance;
            setBettingAmount(maxThreshold);
        }
    };

    const decryptPayload = async(token) => {
        let data = await decryptBettingPayloadResponse(token);
        console.table(data)
    }

    const decryptBettingPayloadResponse = async(data) => {
        let payload = await Crypto.decrypt(data);
        return payload;
    };

    const submitBet = async (type) => {
        setIsSubmittable(false);
        
        if(timerStatus !== "betting_open") {
            toast.info(`${currentRound.id} 회차는 마감되었습니다.`);
            setIsSubmittable(true);
            return;
        }

        userPlaceBet(type);
        const amountToSend = bettingAmount === '' ? '0' : bettingAmount;
        let parameters = {
            ud_game_id: currentRound.id,
            user_bets: type.toLowerCase() === 'up' ? 'up' : 'down',
            amount: amountToSend,
            bets_id: type.toLowerCase() === 'up' ? 'up' : 'down',
        };

        if(+process.env.ALLOWED_ENCRYPTION_DECRYPTION === 1) {
            // const token = await encryptBettingParameters(parameters);
            // parameters = {'rt_token': token};

            const token = await AsymmetricBetCrypto.encrypt(parameters);
            parameters = {'rt_token': token};
        }

        await HLBackendV1Api
            .post(`${process.env.BACKEND_API_URL}/betting-management/createUserBet`, parameters)
            .then((response) => {
                const isError = response.data.statusCode < 200 || response.data.statusCode > 299;

                Swal.fire({
                    title: isError ? (response.data?.message || '오류') : '베팅이 완료되었습니다',
                    text: isError ? 'Please try again later!' : '행운을 빌어요',
                    icon: isError ? 'error' : 'success',
                    showConfirmButton: false,
                    background: '#1d293d',
                    timer: 1000,
                    didRender: () => {
                        Swal.getTitle().style.color = '#b6c2cf';
                        Swal.getHtmlContainer().style.color = '#b6c2cf';
                    },
                });
                
                (!isError) && setBettingAmount('');

                if(response) {
                    setIsSubmittable(true);
                    soundLibrary.success.play();
                    onClose();
                    setBetSuccess(true); // Trigger pending bets in the parent component for socket
                    const { payload } = response.data;
                    console.table(payload);
                }
            })
            .catch((error) => {
                console.log(error.response.data.message);
                toast.error(error.response.data.message);
                soundLibrary.error.play();
                console.log(error);
            }).finally(() => {
                setIsSubmittable(true);
                setBettingAmount('');
            });
    };   

    // Button configuration
    const amountButtons = [
        { value: 10000, label: '1만' },
        { value: 30000, label: '3만' },
        { value: 50000, label: '5만' },
        { value: 100000, label: '10만' },
        { value: 300000, label: '30만' },
        { value: 500000, label: '50만' },
        { value: 1000000, label: '100만' },
    ];

    // Dynamic colors based on bet type
    const colors = {
        up: {
            primary: 'from-emerald-500 to-teal-600',
            secondary: 'from-emerald-400 to-teal-500',
            accent: 'emerald',
            shadow: 'emerald-500/30',
            text: 'text-emerald-400',
            border: 'border-emerald-500/30',
            bg: 'bg-emerald-500/10',
        },
        down: {
            primary: 'from-rose-500 to-pink-600',
            secondary: 'from-rose-400 to-pink-500',
            accent: 'rose',
            shadow: 'rose-500/30',
            text: 'text-rose-400',
            border: 'border-rose-500/30',
            bg: 'bg-rose-500/10',
        }
    };

    const currentColors = colors[type];

    return (
        <>
            <div className="w-full max-w-md mx-auto">
                {/* Glassmorphism Container */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900/90 to-slate-800/90 backdrop-blur-xl border border-white/10 shadow-2xl">
                    
                    {/* Animated Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${currentColors.primary} opacity-5 blur-3xl animate-pulse`} />
                    
                    {/* Content Container */}
                    <div className="relative p-6 space-y-6">
                        
                        {/* Header Section */}
                        <div className="space-y-3">
                            {/* User Info Card */}
                            <div className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentColors.primary} flex items-center justify-center text-white font-bold shadow-lg`}>
                                        {authUser?.nick?.charAt(0) || 'W'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white/90">{authUser?.nick || "wildOne"}</p>
                                        <p className="text-xs text-white/50">Player</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-white/50 mb-1">Balance</p>
                                    <p className="text-lg font-bold text-white flex items-center gap-1">
                                        <span className="text-sm text-white/60">₩</span>
                                        {(authUser?.balance || 0).toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            {/* Round Status */}
                            <div className="flex items-center justify-between px-2">
                                <span className="text-sm text-white/60">Round #{currentRound?.id}</span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    timerStatus === "betting_open" 
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                        : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                }`}>
                                    {timerStatus === "betting_open" ? 'Betting Open' : 'Closed'}
                                </span>
                            </div>
                        </div>

                        {/* Input Section */}
                        <div className="space-y-3">
                            <div className="relative">
                                <input
                                    type="number"
                                    placeholder="Enter amount"
                                    className="w-full h-14 pl-4 pr-24 text-lg bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-2 focus:border-white/20 transition-all duration-300"
                                    value={bettingAmount}
                                    onChange={computeBettingAmount}
                                    min="0"
                                    step="10000"
                                />
                                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                                    <button
                                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                        onClick={() => multiplyBettingAmount(2)}
                                    >
                                        x2
                                    </button>
                                    {/* replaced by clear button
                                    <button
                                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                        onClick={() => multiplyBettingAmount(3)}
                                    >
                                        x3
                                    </button> */}
                                    <button
                                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                        onClick={() => setBettingAmount('')}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
                                            <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm2.78-4.22a.75.75 0 0 1-1.06 0L8 9.06l-1.72 1.72a.75.75 0 1 1-1.06-1.06L6.94 8 5.22 6.28a.75.75 0 0 1 1.06-1.06L8 6.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L9.06 8l1.72 1.72a.75.75 0 0 1 0 1.06Z" clipRule="evenodd" />
                                        </svg>

                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Quick Amount Buttons - FIXED */}
                        <div className="space-y-2">
                            <p className="text-xs font-medium text-white/50 uppercase tracking-wider px-1">Quick Amounts</p>
                            <div className="grid grid-cols-4 gap-2">
                                {amountButtons.map((btn) => (
                                    <button
                                        key={btn.value}
                                        className="group relative overflow-hidden p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg"
                                        onClick={() => computeBettingAmountButton(btn.value)}
                                    >
                                        <div className={`absolute inset-0 bg-gradient-to-br ${currentColors.primary} opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
                                        <span className="relative text-xs font-medium text-white/80 group-hover:text-white">
                                            {btn.label}
                                        </span>
                                    </button>
                                ))}
                                <button
                                    className="group relative overflow-hidden p-3 bg-gradient-to-br from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 border border-white/10 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg shadow-orange-500/30"
                                    onClick={computeBettingAmountButtonAllIn}
                                >
                                    <span className="relative text-xs font-bold text-white">
                                        올인
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="divider before:bg-white/10 after:bg-white/10 text-white/30 text-xs my-2">
                            CONFIRM BET
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            {/* Main Bet Button */}
                            <button 
                                className={`group relative w-full overflow-hidden rounded-xl bg-gradient-to-r ${currentColors.primary} p-4 text-white font-bold text-lg shadow-xl ${currentColors.shadow} transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
                                onClick={() => submitBet(type)}
                                disabled={!isSubmittable || timerStatus !== "betting_open"}
                                onMouseEnter={() => setIsHovering(true)}
                                onMouseLeave={() => setIsHovering(false)}
                            >
                                {/* Animated shine effect */}
                                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transition-transform duration-1000 ${
                                    isHovering ? 'translate-x-full' : '-translate-x-full'
                                }`} />
                                
                                <span className="relative flex items-center justify-center gap-2">
                                    {type === "up" ? (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                            </svg>
                                            <span>업 베팅</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                            </svg>
                                            <span>다운 베팅</span>
                                        </>
                                    )}
                                </span>
                            </button>

                            {/* Clear Button 
                            <button 
                                className="group w-full p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                                onClick={() => setBettingAmount('')}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    className="w-4 h-4 transition-transform duration-500 group-hover:rotate-180"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M8 3.5c-.771 0-1.537.022-2.297.066a1.124 1.124 0 0 0-1.058 1.028l-.018.214a.75.75 0 0 1-1.495-.12l.018-.221a2.624 2.624 0 0 1 2.467-2.399 41.628 41.628 0 0 1 4.766 0 2.624 2.624 0 0 1 2.467 2.399c.056.662.097 1.329.122 2l.748-.748a.75.75 0 1 1 1.06 1.06l-2 2.001a.75.75 0 0 1-1.061 0l-2-1.999a.75.75 0 0 1 1.061-1.06l.689.688a39.89 39.89 0 0 0-.114-1.815 1.124 1.124 0 0 0-1.058-1.028A40.138 40.138 0 0 0 8 3.5ZM3.22 7.22a.75.75 0 0 1 1.061 0l2 2a.75.75 0 1 1-1.06 1.06l-.69-.69c.025.61.062 1.214.114 1.816.048.56.496.996 1.058 1.028a40.112 40.112 0 0 0 4.594 0 1.124 1.124 0 0 0 1.058-1.028 39.2 39.2 0 0 0 .018-.219.75.75 0 1 1 1.495.12l-.018.226a2.624 2.624 0 0 1-2.467 2.399 41.648 41.648 0 0 1-4.766 0 2.624 2.624 0 0 1-2.467-2.399 41.395 41.395 0 0 1-.122-2l-.748.748A.75.75 0 1 1 1.22 9.22l2-2Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span>Clear Amount</span>
                            </button>
                            */}
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="bottom-left"
                theme="dark"
                autoClose={300}
                transition={Bounce}
                hideProgressBar={true}
                toastStyle={{
                    background: '#1e293b',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                }}
            />
        </>
    );
}

export default MobileBettingForm;